import { Box, Grid, useMediaQuery, Typography, type Theme } from '@mui/material';
import { ProfileAvatar } from '@care/react-component-lib';
import { formatStartDate } from '@/utils/formatStartDate';
import { JOB_TYPES } from '@/constants';
import ReadMore from '@/components/ReadMore';
import { getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_jobSearchResults_seoSegmentationJob as SeoSegmentationJob } from 'src/__generated__/getSearchResults';

interface JobSearchResultProps {
  job: SeoSegmentationJob;
  isClientSearchResultsQuery: boolean;
}

const JobSearchResult = ({ job, isClientSearchResultsQuery }: JobSearchResultProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const {
    job: { startDate, title, hourlyRateRange },
    city,
    state,
    seekerAvatar,
    description,
    type,
  } = job;

  const renderHourlyRateRange = () => {
    let output = '';
    if (hourlyRateRange && hourlyRateRange.hourlyRateTo && hourlyRateRange.hourlyRateFrom) {
      // \u2013 = EN DASH; \u2022 = Bullet
      output = `$${hourlyRateRange.hourlyRateFrom.amount} \u2013 $${hourlyRateRange.hourlyRateTo.amount}/hr \u2022`;
    }
    return output;
  };

  const formattedStartDate = formatStartDate(startDate);

  const profileAvatarProps = {
    alt: `Photo for ${title}`,
    size: isMobileOrSmaller ? 'large' : 'xLarge',
    src: seekerAvatar ?? undefined,
    slotProps: {
      avatar: {
        imgProps: isClientSearchResultsQuery
          ? { loading: 'eager' as const }
          : { loading: 'lazy' as const },
      },
    },
    'data-testid': 'seekerAvatar',
  } as const;

  const getElongatedTitle = () => {
    const defaultText = 'About who needs care: ';
    const initialDescription = description.includes(defaultText)
      ? description.slice(defaultText.length)
      : description;

    return `${title} - ${initialDescription}`.slice(0, 65).concat('...');
  };

  const elongatedTitle = getElongatedTitle();
  return (
    <Grid item xs={12}>
      <Box display="flex">
        <ProfileAvatar {...profileAvatarProps}>
          <Box
            height="100%"
            justifyContent="center"
            display="flex"
            flexDirection="column"
            alignItems="center">
            <Typography variant="h5" component="div" width="100%">
              {formattedStartDate.month.toLocaleUpperCase()}
            </Typography>
            <Typography
              variant="h5"
              component="div"
              width="100%"
              textAlign="center"
              fontSize={(theme: Theme) =>
                isMobileOrSmaller ? theme.typography.h5.fontSize : theme.typography.h2.fontSize
              }>
              {formattedStartDate.day.toUpperCase()}
            </Typography>
          </Box>
        </ProfileAvatar>
        <Box ml={2}>
          <Typography variant="h3" color="primary">
            <Box
              mb={0.5}
              sx={(theme: Theme) => ({
                overflow: 'hidden',
                display: '-webkit-box',
                '-webkit-line-clamp': 2,
                '-webkit-box-orient': 'vertical',
                [theme.breakpoints.up('sm')]: {
                  '-webkit-line-clamp': 1,
                },
              })}>
              {elongatedTitle}
            </Box>
          </Typography>
          <Typography
            variant="h5"
            color="primary"
            sx={(theme: Theme) => ({
              fontSize: { md: theme.typography.h4.fontSize },
            })}>
            <Box mb={1}>
              {renderHourlyRateRange()} {type ? JOB_TYPES[type] : ''} &bull; Starts{' '}
              {formattedStartDate.month}
              &nbsp;
              {formattedStartDate.day} &bull; {city}, {state}
            </Box>
          </Typography>
          <ReadMore
            gutterBottom
            color="textPrimary"
            component="div"
            variant={isMobileOrSmaller ? 'body3' : 'body2'}
            charLimit={140}
            value={description}
            showMoreLabel="more"
            onClick={(e: { preventDefault: () => any }) => e.preventDefault()}
          />
        </Box>
      </Box>
    </Grid>
  );
};

export default JobSearchResult;
